import { useMemo } from 'react'

import { SkeletonBox, TableFilters } from '@circlefin/components'
import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'

import type { SelectListItem } from '@circlefin/components/lib/SelectList'
import type { FilterBaseProps } from '@circlefin/components/lib/TableFilters/types'
import type {
  Currency as CurrencyType,
  BlockchainRecipientAddressWithMetadata,
} from '@shared/graphql'

export interface CurrencyProps extends Omit<FilterBaseProps, 'name'> {
  /**
   * Unfiltered list of addresses.
   */
  addresses?: BlockchainRecipientAddressWithMetadata[]
  /**
   * Loading.
   */
  loading?: boolean
}

/**
 * Search by Currency.
 */
export const Currency: React.FC<CurrencyProps> = ({
  addresses,
  loading = false,
  ...rest
}) => {
  const { t } = useTranslation('address-book/filters')

  const currencyList = useMemo(() => {
    if (!addresses) return []

    return addresses.reduce<CurrencyType[]>((acc, address) => {
      if (address.currency == null || acc.includes(address.currency)) return acc

      return [...acc, address.currency]
    }, [])
  }, [addresses])

  const items: Array<SelectListItem<string>> = useMemo(() => {
    return [
      {
        value: undefined,
        label: t('table.currency.all'),
      },
      ...currencyList.map((currency) => ({
        label: currency,
        value: currency,
      })),
    ]
  }, [t, currencyList])

  return (
    <SkeletonBox className={classNames('w-full')} loading={loading}>
      <TableFilters.Dropdown
        {...rest}
        items={items}
        name="currency"
        placeholder={t`table.currency.label`}
      />
    </SkeletonBox>
  )
}
